import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../Header';

/**
 * HOW WE BUILD (WHAT WE USE) PAGE
 * Theme: 60% Aqua Haze (#F0F4F8) | 30% River Bed (#4A5568) | 10% Accent (#2B6CB0)
 * Logic: High-contrast headings (#020617) with uppercase tracking.
 */
const WhatWeUse = () => {
  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] font-sans">
      <Header />
      
      {/* 1. HERO SECTION - Industrial & Clean */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#2B6CB0] font-black text-[10px] uppercase tracking-[0.4em] mb-6">Technical Architecture</p>
            <h1 className="text-5xl md:text-8xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.9]">
              How We <br className="hidden md:block" /> 
              <span className="text-[#2B6CB0]">Build</span>
            </h1>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto mb-12 leading-relaxed font-medium">
              We abandon generic builders for high-performance custom code. From domain procurement to 
              optimized hosting, we handle the heavy lifting while securing the best market prices for your infrastructure.
            </p>
            
            <Link to="/contact" className="inline-block bg-[#2B6CB0] text-white px-10 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-[#020617] transition-all shadow-xl shadow-[#2B6CB0]/20">
              Start Your Build
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 2. CORE METHODOLOGY - Content Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-32">
            <div>
              <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.85]">
                Custom Code <br/> <span className="text-[#2B6CB0]">& eCommerce</span>
              </h2>
              <p className="text-lg text-[#4A5568] mb-8 leading-relaxed font-medium">
                We believe your business shouldn't be limited by template constraints. Our custom-coded solutions 
                provide superior speed, better SEO, and unique user experiences that outpace the competition.
              </p>
              <div className="bg-white p-6 rounded-2xl border border-[#4A5568]/10 shadow-sm">
                <p className="text-sm text-[#4A5568] italic font-medium">
                  "By bypassing 'all-in-one' builders, we reduce your monthly overhead. We find the most 
                  competitive prices for enterprise-grade hosting, ensuring you own your assets entirely."
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -bottom-6 -right-6 w-full h-full bg-[#2B6CB0]/5 rounded-2xl z-0"></div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
                alt="Technical Workspace"
                className="relative z-10 w-full h-[450px] object-cover rounded-2xl shadow-2xl grayscale hover:grayscale-0 transition-all duration-700 border-4 border-white"
              />
            </div>
          </div>

          {/* 3. INFRASTRUCTURE GRID - The Services */}
          <div className="mb-32">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-[#020617] uppercase">
                Infrastructure <br/> <span className="text-[#2B6CB0]">& Support</span>
              </h2>
              <div className="h-[2px] flex-grow bg-[#4A5568]/10 mx-8 hidden lg:block"></div>
              <p className="text-xs font-black uppercase tracking-[0.3em] text-[#4A5568]">Full-Stack Ownership</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { title: 'Custom Development', desc: 'Hand-crafted React and Next.js applications tailored for high-conversion eCommerce.' },
                { title: 'Domain Management', desc: 'Professional domain purchase, DNS configuration, and secure ownership transfer.' },
                { title: 'Hosting Setup', desc: 'Blazing-fast cloud hosting configuration on Vercel, AWS, or Netlify for 99.9% uptime.' },
                { title: 'Price Optimization', desc: 'We source the best industry rates for third-party tools, saving you hundreds in annual fees.' },
                { title: 'Seamless Connection', desc: 'Full integration between your domain, email servers, and store backend.' },
                { title: 'Scalable Architecture', desc: 'Infrastructure that grows with your traffic without requiring a total rebuild.' }
              ].map((service) => (
                <div key={service.title} className="bg-white p-10 rounded-2xl border border-[#4A5568]/5 shadow-sm hover:shadow-xl hover:border-[#2B6CB0]/20 transition-all group">
                  <div className="w-10 h-1 bg-[#2B6CB0] mb-6 group-hover:w-full transition-all duration-500"></div>
                  <h3 className="text-sm font-black mb-4 text-[#020617] uppercase tracking-widest">{service.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed font-medium">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 4. FINAL CTA - The Conversion Point */}
          <div className="text-center bg-[#020617] p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#2B6CB0]/10 rounded-full -mr-16 -mt-16"></div>
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-white uppercase leading-tight">
              Ready for a <br className="hidden md:block" /> <span className="text-[#2B6CB0]">Better Build?</span>
            </h2>
            <p className="text-xl text-white/60 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Stop overpaying for restrictive builders. Let’s set up your custom ecosystem 
              with professional hosting and domain management today.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-12 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all">
                Get a Quote
              </Link>
              <Link to="/portfolio" className="border-2 border-[#4A5568] text-white px-12 py-5 rounded-xl font-black uppercase tracking-widest text-xs hover:border-[#2B6CB0] hover:text-[#2B6CB0] transition-all">
                See Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeUse;